<template lang="pug">
.delivery-chart
    el-card(shadow="never")
        .card-header(slot="header")
            .header-left
                i.el-icon-fa-chart-line.fas
                span {{ $t('Delivery Timeline') }}
            .header-right
                el-tooltip(v-if="isZoomed" :content="$t('Reset zoom')" placement="top")
                    el-button.reset-zoom(
                        size="mini"
                        circle
                        @click="resetZoom"
                    )
                        i.el-icon-c-scale-to-original
                el-select.granularity-select(
                    v-model="selectedGranularity"
                    size="mini"
                    @change="onGranularityChange"
                )
                    el-option(
                        v-for="option in granularityOptions"
                        :key="option.value"
                        :label="option.label"
                        :value="option.value"
                    )
                //- el-tooltip(:content="$t('Toggle logarithmic scale')" placement="top")
                //-     el-button.scale-toggle(
                //-         size="mini"
                //-         circle
                //-         :type="useLogScale ? 'primary' : 'default'"
                //-         @click="toggleLogScale"
                //-     )
                //-         i.el-icon-fa-chart-line.fas

        .chart-container(v-loading="loading")
            template(v-if="hasData")
                line-chart(
                    ref="chart"
                    :chart-data="chartData"
                    :options="processedChartOptions"
                    :height="250"
                )
                .legend
                    el-tooltip(
                        v-for="dataset in legendItems"
                        :key="dataset.key"
                        :content="$t('Ctrl+click to isolate')"
                        placement="top"
                        :open-delay="500"
                    )
                        el-button.legend-item(
                            size="mini"
                            @click.exact="toggleDataset(dataset.key)"
                            @click.ctrl.exact="isolateDataset(dataset.key)"
                            @click.meta.exact="isolateDataset(dataset.key)"
                        )
                            span.legend-color(:style="{ backgroundColor: dataset.color }")
                            span.legend-label(:class="{ 'strike-through': disabledDatasets.includes(dataset.key) }") {{ dataset.label }}
            .empty-state(v-else-if="!loading")
                i.el-icon-data-line
                p {{ $t('No timeline data available yet.') }}
</template>

<i18n>
{
    "de": {
        "Delivery Timeline": "Versand-Zeitverlauf",
        "Reset zoom": "Zoom zurücksetzen",
        "Opens": "Geöffnet",
        "Clicks": "Geklickt",
        "Bounces": "Bounces",
        "Delivered": "Zugestellt",
        "No timeline data available yet.": "Noch keine Zeitverlaufsdaten verfügbar.",
        "Ctrl+click to isolate": "Strg+Klick zum Isolieren",
        "Toggle logarithmic scale": "Logarithmische Skala umschalten",
        "15 seconds": "15 Sekunden",
        "1 minute": "1 Minute",
        "5 minutes": "5 Minuten",
        "15 minutes": "15 Minuten",
        "30 minutes": "30 Minuten",
        "1 hour": "1 Stunde",
        "6 hours": "6 Stunden",
        "1 day": "1 Tag"
    }
}
</i18n>

<script>
import _ from 'lodash'
import LineChart from '@/components/LineChart'
import { formatDate } from '@/filters'
import { useNewsletterNewsletterStore } from '@/stores'

const GRANULARITY_LABELS = {
    '15s': '15 seconds',
    '1m': '1 minute',
    '5m': '5 minutes',
    '15m': '15 minutes',
    '30m': '30 minutes',
    '1h': '1 hour',
    '6h': '6 hours',
    '1d': '1 day'
}

// Granularity thresholds in seconds - targets ~300-500 data points for fine detail
const GRANULARITY_THRESHOLDS = [
    { maxSeconds: 7200, granularity: '15s' },     // ≤ 2 hours → 15s (~480 points)
    { maxSeconds: 28800, granularity: '1m' },     // ≤ 8 hours → 1m (~480 points)
    { maxSeconds: 86400, granularity: '5m' },     // ≤ 24 hours → 5m (~288 points)
    { maxSeconds: 259200, granularity: '15m' },   // ≤ 3 days → 15m (~288 points)
    { maxSeconds: 604800, granularity: '30m' },   // ≤ 7 days → 30m (~336 points)
    { maxSeconds: 2592000, granularity: '1h' },   // ≤ 30 days → 1h (~720 points)
    { maxSeconds: 7776000, granularity: '6h' }    // ≤ 90 days → 6h (~360 points)
]

export default {
    name: 'DeliveryChart',
    components: {
        LineChart
    },
    props: {
        newsletterId: {
            type: [Number, String],
            required: true
        },
        startedSendingAt: {
            type: [String, Number],
            default: null
        }
    },
    data() {
        return {
            disabledDatasets: [],
            useLogScale: false,
            isZoomed: false,
            isResettingZoom: false,
            zoomBounds: null,
            selectedGranularity: null,
            chartOptions: {
                responsive: true,
                maintainAspectRatio: false,
                parsing: false, // Required for decimation with {x,y} data
                animation: {
                    duration: 500
                },
                plugins: {
                    legend: {
                        display: false
                    },
                    tooltip: {
                        mode: 'index',
                        intersect: false,
                        backgroundColor: 'rgba(48, 49, 51, 0.95)',
                        titleColor: '#fff',
                        bodyColor: '#fff',
                        borderColor: 'rgba(255, 255, 255, 0.1)',
                        borderWidth: 1,
                        padding: 12,
                        titleFont: {
                            size: 13,
                            weight: 'bold'
                        },
                        bodyFont: {
                            size: 12
                        },
                        bodySpacing: 6,
                        boxPadding: 4,
                        usePointStyle: true,
                        callbacks: {
                            title: (items) => {
                                if (!items.length) return ''
                                const date = new Date(items[0].parsed.x)
                                return this.formatTooltipDate(date)
                            },
                            label: (context) => {
                                const label = context.dataset.label || ''
                                const value = context.parsed.y
                                return ` ${label}: ${value}`
                            }
                        }
                    },
                    decimation: {
                        enabled: true,
                        algorithm: 'lttb',
                        samples: 200
                    },
                    zoom: {
                        zoom: {
                            drag: {
                                enabled: true,
                                backgroundColor: 'rgba(64, 158, 255, 0.1)',
                                borderColor: 'rgba(64, 158, 255, 0.8)',
                                borderWidth: 1
                            },
                            mode: 'x',
                            onZoomComplete: ({ chart }) => {
                                // Skip if we're resetting zoom (not a user-initiated zoom)
                                if (this.isResettingZoom) {
                                    return
                                }
                                this.isZoomed = true
                                this.onZoomComplete(chart)
                            }
                        },
                        pan: {
                            enabled: true,
                            mode: 'x',
                            modifierKey: 'ctrl'
                        },
                        limits: {
                            x: { min: 'original', max: 'original' }
                        }
                    }
                },
                scales: {
                    x: {
                        type: 'time',
                        time: {
                            displayFormats: {
                                second: 'HH:mm:ss',
                                minute: 'HH:mm',
                                hour: 'HH:mm',
                                day: 'dd.MM',
                                week: 'dd.MM',
                                month: 'MMM yyyy'
                            }
                        },
                        grid: {
                            drawOnChartArea: false
                        },
                        ticks: {
                            maxTicksLimit: 15,
                            autoSkip: true,
                            autoSkipPadding: 10,
                            maxRotation: 45,
                            minRotation: 0
                            // callback is added in getChartOptions() since cloneDeep doesn't preserve functions
                        }
                    },
                    y: {
                        type: 'linear',
                        beginAtZero: true,
                        ticks: {
                            precision: 0
                        },
                        grid: {
                            drawTicks: true
                        }
                    }
                }
            }
        }
    },
    mounted() {
        // Set initial granularity based on time elapsed
        this.selectedGranularity = this.calculateGranularity()
        this.fetchData()
    },
    watch: {
        chartData: {
            deep: true,
            handler(newData) {
                // Explicitly re-render chart when data changes
                // The reactiveProp mixin doesn't always trigger properly
                this.$nextTick(() => {
                    const hasData = newData && newData.datasets && newData.datasets.length > 0 &&
                        newData.datasets.some(ds => ds.data && ds.data.length > 0)
                    if (this.$refs.chart && hasData) {
                        this.$refs.chart.renderChart(newData, this.getChartOptions())
                    }
                })
            }
        }
    },
    computed: {
        newsletterStore() {
            return useNewsletterNewsletterStore()
        },
        statisticsTimeline() {
            return this.newsletterStore.statisticsTimeline
        },
        loading() {
            return _.get(this.statisticsTimeline, 'loading', false)
        },
        timelineData() {
            return _.get(this.statisticsTimeline, 'data', [])
        },
        granularity() {
            return _.get(this.statisticsTimeline, 'granularity', '1h')
        },
        granularityOptions() {
            return [
                { value: '15s', label: this.$t('15 seconds') },
                { value: '1m', label: this.$t('1 minute') },
                { value: '5m', label: this.$t('5 minutes') },
                { value: '15m', label: this.$t('15 minutes') },
                { value: '30m', label: this.$t('30 minutes') },
                { value: '1h', label: this.$t('1 hour') },
                { value: '6h', label: this.$t('6 hours') },
                { value: '1d', label: this.$t('1 day') }
            ]
        },
        hasData() {
            return this.timelineData && this.timelineData.length > 0
        },
        chartData() {
            if (!this.hasData) {
                return { datasets: [] }
            }

            const pointRadius = 0
            const pointHoverRadius = 5
            const pointHitRadius = 10

            // Disable animations for large datasets
            if (this.timelineData.length > 200) {
                this.chartOptions.animation.duration = 0
            } else {
                this.chartOptions.animation.duration = 500
            }

            // Convert to {x, y} format with timestamps for time scale + decimation
            const toXY = (point, valueKey) => ({
                x: new Date(point.period).getTime(),
                y: point[valueKey]
            })

            return {
                datasets: [
                    {
                        key: 'delivered',
                        label: this.$t('Delivered'),
                        borderColor: '#67c23a',
                        backgroundColor: '#67c23a',
                        fill: false,
                        hidden: this.disabledDatasets.includes('delivered'),
                        data: this.timelineData.map(point => toXY(point, 'delivered')),
                        pointRadius,
                        pointHoverRadius,
                        pointHitRadius,
                        tension: 0.4,
                        borderWidth: 2
                    },
                    {
                        key: 'opens',
                        label: this.$t('Opens'),
                        borderColor: '#409eff',
                        backgroundColor: '#409eff',
                        fill: false,
                        hidden: this.disabledDatasets.includes('opens'),
                        data: this.timelineData.map(point => toXY(point, 'opens')),
                        pointRadius,
                        pointHoverRadius,
                        pointHitRadius,
                        tension: 0.4,
                        borderWidth: 2
                    },
                    {
                        key: 'clicks',
                        label: this.$t('Clicks'),
                        borderColor: '#6E26FF',
                        backgroundColor: '#6E26FF',
                        fill: false,
                        hidden: this.disabledDatasets.includes('clicks'),
                        data: this.timelineData.map(point => toXY(point, 'clicks')),
                        pointRadius,
                        pointHoverRadius,
                        pointHitRadius,
                        tension: 0.4,
                        borderWidth: 2
                    },
                    {
                        key: 'bounces',
                        label: this.$t('Bounces'),
                        borderColor: '#f56c6c',
                        backgroundColor: '#f56c6c',
                        fill: false,
                        hidden: this.disabledDatasets.includes('bounces'),
                        data: this.timelineData.map(point => toXY(point, 'bounces')),
                        pointRadius,
                        pointHoverRadius,
                        pointHitRadius,
                        tension: 0.4,
                        borderWidth: 2
                    }
                ]
            }
        },
        legendItems() {
            return [
                { key: 'delivered', label: this.$t('Delivered'), color: '#67c23a' },
                { key: 'opens', label: this.$t('Opens'), color: '#409eff' },
                { key: 'clicks', label: this.$t('Clicks'), color: '#6E26FF' },
                { key: 'bounces', label: this.$t('Bounces'), color: '#f56c6c' }
            ]
        },
        processedChartOptions() {
            return this.getChartOptions()
        }
    },
    methods: {
        fetchStatisticsTimeline(payload) {
            return this.newsletterStore.fetchStatisticsTimeline(payload)
        },
        getGranularityForTimeRange(seconds) {
            for (const threshold of GRANULARITY_THRESHOLDS) {
                if (seconds <= threshold.maxSeconds) {
                    return threshold.granularity
                }
            }
            return '1d' // Default for very long timespans
        },
        calculateGranularity() {
            if (!this.startedSendingAt) {
                return '1h' // Default granularity
            }

            // Handle both Unix timestamp (number/seconds) and date string
            let startTime
            if (typeof this.startedSendingAt === 'number') {
                startTime = this.startedSendingAt * 1000 // Convert seconds to milliseconds
            } else {
                startTime = new Date(this.startedSendingAt).getTime()
            }

            const now = Date.now()
            const elapsedSeconds = Math.floor((now - startTime) / 1000)

            return this.getGranularityForTimeRange(elapsedSeconds)
        },
        fetchData(options = {}) {
            const silent = options.silent || false
            const granularity = this.selectedGranularity || this.calculateGranularity()
            const payload = {
                newsletterId: this.newsletterId,
                granularity: granularity,
                silent: silent
            }

            // Pass zoom bounds as start/end if zoomed
            if (this.zoomBounds) {
                payload.start = this.zoomBounds.min
                payload.end = this.zoomBounds.max
            }

            this.fetchStatisticsTimeline(payload)
        },
        onGranularityChange() {
            this.fetchData({ silent: true })
        },
        toggleDataset(key) {
            const index = this.disabledDatasets.indexOf(key)
            if (index >= 0) {
                this.disabledDatasets.splice(index, 1)
            } else {
                this.disabledDatasets.push(key)
            }
            this.rerenderChart()
        },
        isolateDataset(key) {
            const allKeys = ['delivered', 'opens', 'clicks', 'bounces']
            const otherKeys = allKeys.filter(k => k !== key)

            // If all others are already disabled, show all
            const allOthersDisabled = otherKeys.every(k => this.disabledDatasets.includes(k))
            if (allOthersDisabled) {
                this.disabledDatasets = []
            } else {
                // Disable all except the clicked one
                this.disabledDatasets = otherKeys
            }
            this.rerenderChart()
        },
        toggleLogScale() {
            this.useLogScale = !this.useLogScale
            this.rerenderChart()
        },
        getChartOptions() {
            const baseOptions = _.cloneDeep(this.chartOptions)

            // Apply zoom bounds if zoomed, otherwise use full data range
            if (this.zoomBounds) {
                baseOptions.scales.x.min = this.zoomBounds.min
                baseOptions.scales.x.max = this.zoomBounds.max
            } else if (this.timelineData && this.timelineData.length > 0) {
                // Lock x-axis bounds to prevent shifting when toggling scale
                const timestamps = this.timelineData.map(p => new Date(p.period).getTime())
                baseOptions.scales.x.min = Math.min(...timestamps)
                baseOptions.scales.x.max = Math.max(...timestamps)
            }

            if (this.useLogScale) {
                baseOptions.scales.y = {
                    type: 'logarithmic',
                    min: 1,
                    ticks: {
                        precision: 0,
                        callback: function(value) {
                            const normalized = value / Math.pow(10, Math.floor(Math.log10(value)))
                            return (normalized === 1 || normalized === 2 || normalized === 5) ? value : null
                        }
                    },
                    grid: {
                        drawTicks: true
                    }
                }
            }

            return baseOptions
        },
        rerenderChart() {
            this.$nextTick(() => {
                if (this.$refs.chart) {
                    this.$refs.chart.renderChart(this.chartData, this.getChartOptions())
                }
            })
        },
        resetZoom() {
            // Set flag to prevent onZoomComplete from re-enabling isZoomed
            this.isResettingZoom = true
            this.isZoomed = false
            this.zoomBounds = null

            // Reset Chart.js zoom plugin state via LineChart method
            if (this.$refs.chart && this.$refs.chart.resetZoom) {
                this.$refs.chart.resetZoom()
            }

            // Clear the flag after a tick to ensure the callback has fired
            this.$nextTick(() => {
                this.isResettingZoom = false
            })

            // Recalculate appropriate granularity for full view
            this.selectedGranularity = this.calculateGranularity()

            // Fetch full timeline data (no start/end since zoomBounds is null)
            this.fetchData({ silent: true })
        },
        onZoomComplete(chart) {
            // Store zoom bounds for potential future use
            const xScale = chart.scales.x
            if (xScale) {
                this.zoomBounds = { min: xScale.min, max: xScale.max }
            }
        },
        formatPeriod(period) {
            if (!period) return ''
            const date = new Date(period)

            // Format based on granularity
            switch (this.granularity) {
            case '15s':
            case '1m':
                // Show HH:mm:ss for seconds/minute granularity
                return formatDate(date, 'HH:mm:ss')
            case '5m':
            case '15m':
            case '30m':
                // Show HH:mm for sub-hour granularity
                return formatDate(date, 'HH:mm')
            case '1h':
                // Show HH:00 for hourly
                return formatDate(date, 'HH:mm')
            case '6h':
                // Show dd.MM HH:mm for 6-hour blocks
                return formatDate(date, 'dd.MM HH:mm')
            case '1d':
                // Show dd.MM for daily
                return formatDate(date, 'dd.MM')
            default:
                return formatDate(date, 'HH:mm')
            }
        },
        formatTooltipDate(date) {
            // Format tooltip title based on granularity
            switch (this.granularity) {
            case '15s':
            case '1m':
                return formatDate(date, 'dd.MM.yyyy HH:mm:ss')
            case '5m':
            case '15m':
            case '30m':
            case '1h':
                return formatDate(date, 'dd.MM.yyyy HH:mm')
            case '6h':
            case '1d':
                return formatDate(date, 'dd.MM.yyyy')
            default:
                return formatDate(date, 'dd.MM.yyyy HH:mm')
            }
        }
    }
}
</script>

<style lang="sass" scoped>
.delivery-chart
    margin-top: 20px

    ::v-deep .el-card__header
        padding: 12px 20px !important
        background: #fff
        border-bottom: 1px solid #e4e7ed

    ::v-deep .el-card__body
        padding: 20px !important

    .card-header
        display: flex
        justify-content: space-between
        align-items: center

        .header-left
            display: flex
            align-items: center
            gap: 8px
            font-weight: 600
            font-size: 14px
            color: #303133

            i
                color: #409eff
                font-size: 14px

        .header-right
            display: flex
            align-items: center
            gap: 8px

            .scale-toggle
                padding: 7px 8px

            .granularity-select
                width: 130px

.chart-container
    min-height: 280px

.legend
    display: flex
    justify-content: center
    gap: 8px
    margin-top: 16px
    padding-top: 16px
    border-top: 1px solid #e4e7ed

    .legend-item.el-button
        position: relative
        padding: 7px 12px 7px 28px
        border: none
        background: none !important

        &:hover
            background: #f5f7fa !important

        .legend-color
            position: absolute
            left: 8px
            top: 50%
            transform: translateY(-50%)
            width: 14px
            height: 14px
            border-radius: 50%
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15)

        .legend-label
            font-size: 13px
            color: #606266

            &.strike-through
                text-decoration: line-through
                text-decoration-thickness: 2px
                color: #909399

.empty-state
    display: flex
    flex-direction: column
    align-items: center
    justify-content: center
    padding: 60px 20px
    color: #909399

    i
        font-size: 48px
        margin-bottom: 16px

    p
        margin: 0
        font-size: 14px
</style>
